'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { AuthMode } from './AuthScreen';
import { useAuth } from '@/contexts/AuthContext';

interface SignUpFormData {
  display_name: string;
  email: string;
  password: string;
  confirm_password: string;
  terms: boolean;
}

interface SignUpFormProps {
  onSwitch: (mode: AuthMode) => void;
}

export default function SignUpForm({ onSwitch }: SignUpFormProps) {
  const router = useRouter();
  const { signUp } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<SignUpFormData>();

  const password = watch('password');

  const onSubmit = async (data: SignUpFormData) => {
    try {
      await signUp(data.email, data.password, { fullName: data.display_name, displayName: data.display_name });
      toast.success(`Account created! Welcome to StudyMate, ${data.display_name}.`);
      router.push('/dashboard');
    } catch (err: any) {
      const msg = err?.message || 'Sign up failed. Please try again.';
      if (msg.toLowerCase().includes('already registered') || msg.toLowerCase().includes('already exists')) {
        toast.error('An account with this email already exists. Try signing in instead.');
      } else {
        toast.error(msg);
      }
    }
  };

  return (
    <div className="animate-slide-up">
      {/* Mobile logo */}
      <div className="lg:hidden flex items-center gap-2 mb-8">
        <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center">
          <span className="text-white font-bold text-sm">SM</span>
        </div>
        <span className="font-bold text-xl text-foreground">StudyMate</span>
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-foreground mb-1">Create your account</h2>
        <p className="text-muted-foreground text-sm">No email verification — start organizing immediately.</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
        {/* Display name */}
        <div>
          <label htmlFor="signup-name" className="block text-sm font-medium text-foreground mb-1.5">
            Display name
          </label>
          <p className="text-xs text-muted-foreground mb-1.5">This is how we'll greet you on the dashboard.</p>
          <input
            id="signup-name"
            type="text"
            autoComplete="name"
            placeholder="Your first name or nickname"
            className={`w-full px-3.5 py-2.5 rounded-lg border text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all ${
              errors.display_name ? 'border-destructive' : 'border-border'
            }`}
            {...register('display_name', {
              required: 'Display name is required.',
              minLength: { value: 2, message: 'Name must be at least 2 characters.' },
              maxLength: { value: 40, message: 'Name must be 40 characters or fewer.' },
            })}
          />
          {errors.display_name && (
            <p className="mt-1.5 text-xs text-destructive">{errors.display_name.message}</p>
          )}
        </div>

        {/* Email */}
        <div>
          <label htmlFor="signup-email" className="block text-sm font-medium text-foreground mb-1.5">
            University email
          </label>
          <input
            id="signup-email"
            type="email"
            autoComplete="email"
            placeholder="you@university.edu"
            className={`w-full px-3.5 py-2.5 rounded-lg border text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all ${
              errors.email ? 'border-destructive' : 'border-border'
            }`}
            {...register('email', {
              required: 'Email is required.',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address.' },
            })}
          />
          {errors.email && (
            <p className="mt-1.5 text-xs text-destructive">{errors.email.message}</p>
          )}
        </div>

        {/* Password */}
        <div>
          <label htmlFor="signup-password" className="block text-sm font-medium text-foreground mb-1.5">
            Password
          </label>
          <p className="text-xs text-muted-foreground mb-1.5">At least 8 characters, mix of letters and numbers recommended.</p>
          <div className="relative">
            <input
              id="signup-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Create a strong password"
              className={`w-full px-3.5 py-2.5 pr-10 rounded-lg border text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all ${
                errors.password ? 'border-destructive' : 'border-border'
              }`}
              {...register('password', {
                required: 'Password is required.',
                minLength: { value: 8, message: 'Password must be at least 8 characters.' },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1.5 text-xs text-destructive">{errors.password.message}</p>
          )}
        </div>

        {/* Confirm password */}
        <div>
          <label htmlFor="signup-confirm" className="block text-sm font-medium text-foreground mb-1.5">
            Confirm password
          </label>
          <div className="relative">
            <input
              id="signup-confirm"
              type={showConfirm ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Re-enter your password"
              className={`w-full px-3.5 py-2.5 pr-10 rounded-lg border text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all ${
                errors.confirm_password ? 'border-destructive' : 'border-border'
              }`}
              {...register('confirm_password', {
                required: 'Please confirm your password.',
                validate: (val) => val === password || 'Passwords do not match.',
              })}
            />
            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label={showConfirm ? 'Hide password' : 'Show password'}
            >
              {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.confirm_password && (
            <p className="mt-1.5 text-xs text-destructive">{errors.confirm_password.message}</p>
          )}
        </div>

        {/* Terms */}
        <div>
          <div className="flex items-start gap-2.5">
            <input
              id="terms"
              type="checkbox"
              className="w-4 h-4 rounded border-border accent-primary mt-0.5 flex-shrink-0"
              {...register('terms', { required: 'You must accept the terms to continue.' })}
            />
            <label htmlFor="terms" className="text-sm text-muted-foreground cursor-pointer leading-relaxed">
              I agree to the{' '}
              <span className="text-primary hover:underline cursor-pointer">Terms of Service</span>
              {' '}and{' '}
              <span className="text-primary hover:underline cursor-pointer">Privacy Policy</span>
            </label>
          </div>
          {errors.terms && (
            <p className="mt-1.5 text-xs text-destructive">{errors.terms.message}</p>
          )}
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full py-2.5 bg-primary text-primary-foreground text-sm font-semibold rounded-lg hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-60 disabled:cursor-not-allowed transition-all active:scale-[0.98] flex items-center justify-center gap-2"
          style={{ minHeight: '42px' }}
        >
          {isSubmitting ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              <span>Creating account...</span>
            </>
          ) : (
            'Create account'
          )}
        </button>
      </form>

      <p className="mt-6 text-center text-sm text-muted-foreground">
        Already have an account?{' '}
        <button
          onClick={() => onSwitch('login')}
          className="text-primary font-semibold hover:underline"
        >
          Sign in
        </button>
      </p>
    </div>
  );
}